import * as React from 'react'
import clsx from 'clsx'
import {
  SelectClasses,
  SelectAdvanceInputProps,
  SelectAdvanceInput,
} from './SelectAdvanceInput'
import { StandardInput, StandardInputProps } from '../StandardInput'
import { OutlinedInput } from '../OutlinedInput'
import { OutlinedDashInput } from '../OutlinedDashInput'
import { FilledInput } from '../FilledInput'
import { SxProps } from '../sx'
import {
  useFormControl,
  formControlState,
  FormControlProps,
} from '../FormControl'
import { useForkRef } from '../../hooks'
import { deepmerge } from '../../utils'
import CaretDownIcon from '../internal/svg-icons/CaretDown'
import style from './index.module.scss'

export interface SelectAdvanceProps<T> extends SelectAdvanceInputProps<T> {
  /**
   * If `true`, the width of the popover will automatically be set according to the items inside the
   * menu, otherwise it will be at least the width of the select input.
   * @default false
   */
  autoWidth?: boolean
  /**
   * Override or extend the styles applied to the component.
   * @default {}
   */
  classes?: Partial<SelectClasses>
  /**
   * The icon that displays the arrow.
   * @default CaretDownIcon
   */
  IconComponent?: React.ElementType
  /**
   * An `Input` element; does not have to be a material-ui specific `Input`.
   */
  input?: React.ReactElement<any, any>
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   * When `native` is `true`, the attributes are applied on the `select` element.
   */
  inputProps?: StandardInputProps['inputProps']
  /**
   * Props applied to the clickable div element.
   */
  SelectDisplayProps?: React.HTMLAttributes<HTMLDivElement>
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The variant to use.
   * @default 'outlined'
   */
  variant?: FormControlProps['variant']
}

function SelectAdvanceFn<T>(
  props: SelectAdvanceProps<T>,
  ref: React.ForwardedRef<HTMLSelectElement>
) {
  const {
    autoWidth = false,
    classes = {},
    IconComponent = CaretDownIcon,
    input,
    inputProps,
    SelectDisplayProps,
    variant: variantProp = 'outlined',
    data,
    autoHighlight,
    componentName,
    disableCloseOnSelect,
    disabled,
    disabledItemsFocusable,
    disableListWrap,
    getOptionDisabled,
    renderValue,
    getOptionLabel,
    handleHomeEndKeys,
    id,
    inputValue,
    multiple,
    onClose,
    onHighlightChange,
    onInputChange,
    onOpen,
    open,
    value,
    defaultValue,
    rowHeight,
    overscan,
    pageSize,
    rowCount,
    loading,
    getRowId,
    onFetchRows,
    onChange,
    columns,
    clearOnBlur,
    classNamePrefix,
    renderOption,
    getRowCanExpand,
    getSubRows,
    enableSubRowSelection,
    getIsRowExpanded,
    defaultExpanded,
    isRowLoading,
    slotProps,
    hideSearch,
    renderListItem,
    renderListItemSkeleton,
    slots,
    apiRef,
    onFetchSubRows,
    renderContentTop,
    renderContentBottom,
    hideSelectNav,
    overlay,
    allowClearingSingleSelect,
    onSelectAll,
    ...other
  } = props

  const factorFormControl = useFormControl()
  const fcs = formControlState({
    props,
    factorFormControl,
    states: ['variant', 'error', 'focused', 'size', 'readOnly'],
  })
  const variant = (fcs.variant || variantProp) as FormControlProps['variant']

  const InputComponent =
    input ||
    {
      standard: <StandardInput />,
      outlined: <OutlinedInput />,
      filled: <FilledInput />,
      outlinedDash: <OutlinedDashInput />,
    }[variant!]

  // @ts-ignore
  const inputComponentRef = useForkRef(ref, InputComponent.ref)

  return (
    <React.Fragment>
      {React.cloneElement(InputComponent, {
        // Most of the logic is implemented in `SelectInput`.
        // The `Select` component is a simple API wrapper to expose something better to play with.
        inputComponent: SelectAdvanceInput,
        classes: {
          ...InputComponent.props.classes,
          root: clsx(
            InputComponent.props?.classes?.root,
            style.selectInputRoot,
            style[`size-${fcs.size}`]
          ),
        },
        ref: inputComponentRef,
        className: clsx(InputComponent.props.className),
        // If a custom input is provided via 'input' prop, do not allow 'variant' to be propagated to it's root element. See https://github.com/mui/material-ui/issues/33894.
        ...(!input && { variant }),
        inputProps: {
          error: fcs.error,
          size: fcs.size,
          readOnly: fcs.readOnly,
          IconComponent,
          variant,
          type: undefined, // We render a select. We can ignore the type provided by the `Input`.
          autoWidth,
          // Start of AdvanceSelect Props
          data,
          autoHighlight,
          componentName,
          disableCloseOnSelect,
          disabled,
          disabledItemsFocusable,
          disableListWrap,
          getOptionDisabled,
          renderValue,
          getOptionLabel,
          handleHomeEndKeys,
          id,
          inputValue,
          multiple,
          onClose,
          onHighlightChange,
          onInputChange,
          onOpen,
          open,
          value,
          defaultValue,
          rowHeight,
          overscan,
          pageSize,
          rowCount,
          loading,
          getRowId,
          onFetchRows,
          onChange,
          columns,
          clearOnBlur,
          classNamePrefix,
          renderOption,
          getRowCanExpand,
          getSubRows,
          enableSubRowSelection,
          getIsRowExpanded,
          defaultExpanded,
          slotProps,
          slots,
          renderListItem,
          renderListItemSkeleton,
          isRowLoading,
          apiRef,
          onFetchSubRows,
          renderContentTop,
          renderContentBottom,
          hideSelectNav,
          hideSearch,
          overlay,
          allowClearingSingleSelect,
          onSelectAll,
          // End of AdvanceSelect Props
          SelectDisplayProps: { id, ...SelectDisplayProps },
          ...inputProps,
          classes: {
            ...(inputProps ? deepmerge(classes, inputProps.classes) : classes),
          },
          ...(input ? input.props.inputProps : {}),
        },
        ...other,
      })}
    </React.Fragment>
  )
}

export const SelectAdvance = React.forwardRef(SelectAdvanceFn) as <T>(
  props: SelectAdvanceProps<T>,
  ref?: React.ForwardedRef<HTMLSelectElement>
) => ReturnType<typeof SelectAdvanceFn>

// @ts-ignore - We add this property to find this component when wrapped in FormControl
SelectAdvance.factorName = 'Select'
